import { useEffect, useState } from "react";
import { Logo } from "./Logo";
import { Menu, X, Sun, Moon, Lock } from "lucide-react";

const LINKS = [
  { label: "Coverage", href: "#coverage" },
  { label: "About", href: "#about" },
  { label: "Stories", href: "#stories" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = ({ theme, onToggleTheme }) => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className="fixed inset-x-0 top-4 z-50 px-4">
      <nav
        data-testid="main-nav"
        className={`mx-auto flex max-w-6xl items-center justify-between gap-4 rounded-full border border-[color:var(--ns-line)] px-5 py-3 backdrop-blur-xl transition-shadow duration-500 ${
          scrolled ? "bg-[color:var(--ns-bg)]/90 shadow-2xl shadow-black/20" : "bg-[color:var(--ns-bg)]/50"
        }`}
      >
        <a href="#top" data-testid="nav-logo-link" className="flex items-center gap-3">
          <Logo size={34} testId="nav-logo" />
          <span className="leading-none">
            <span className="block font-serif text-lg tracking-wide text-[color:var(--ns-ink)]">Hunt Shield</span>
            <span className="block text-[9px] font-semibold uppercase tracking-[0.28em] text-[color:var(--ns-gold)]">
              Insurance Group
            </span>
          </span>
        </a>

        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`nav-${l.label.toLowerCase()}-link`}
              href={l.href}
              className="text-sm text-[color:var(--ns-muted)] transition-colors duration-300 hover:text-[color:var(--ns-gold)]"
            >
              {l.label}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-phone-link"
            href="tel:+14047032343"
            className="hidden font-mono text-sm text-[color:var(--ns-muted)] transition-colors hover:text-[color:var(--ns-gold)] md:block"
          >
            404-703-2343
          </a>
          <button
            onClick={onToggleTheme}
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
            className="rounded-full border border-[color:var(--ns-line)] p-2 text-[color:var(--ns-muted)] transition-colors hover:text-[color:var(--ns-gold)]"
          >
            {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
          </button>
          <a
            href="/admin/login"
            data-testid="nav-admin-link"
            aria-label="Admin portal"
            className="rounded-full border border-[color:var(--ns-line)] p-2 text-[color:var(--ns-muted)] transition-colors hover:text-[color:var(--ns-gold)]"
          >
            <Lock size={15} />
          </a>
          <a
            href="#quote"
            data-testid="nav-quote-btn"
            className="hidden items-center rounded-full bg-[color:var(--ns-gold)] px-5 py-2 text-sm font-semibold text-[#0B0F17] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/25 sm:inline-flex"
          >
            Get a Quote
          </a>
          <button
            className="p-2 text-[color:var(--ns-ink)] lg:hidden"
            data-testid="nav-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </nav>

      {open && (
        <div
          data-testid="mobile-menu"
          className="mx-auto mt-2 flex max-w-6xl flex-col gap-4 rounded-2xl border border-[color:var(--ns-line)] bg-[color:var(--ns-bg)]/95 p-6 backdrop-blur-xl lg:hidden"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-lg text-[color:var(--ns-ink)]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#quote"
            data-testid="mobile-nav-quote-btn"
            onClick={() => setOpen(false)}
            className="mt-2 inline-flex justify-center rounded-full bg-[color:var(--ns-gold)] px-5 py-3 font-semibold text-[#0B0F17]"
          >
            Get a Quote
          </a>
        </div>
      )}
    </header>
  );
};
